import { MenuItem } from "../../types/types";
import { Link, useNavigate } from "react-router-dom";
import navStyle from "./navbar.module.scss";

interface SubMenuProps {
  items?: MenuItem[];
  onClose: () => void;
  onBack?: () => void;
}

const ExternalIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.8221 2.17798C17.9725 2.32842 18.0457 2.52686 18.0416 2.72399V8.54165C18.0416 8.95586 17.7058 9.29165 17.2916 9.29165C16.8774 9.29165 16.5416 8.95586 16.5416 8.54165V4.51914L10.3221 10.7386C10.0292 11.0315 9.55431 11.0315 9.26142 10.7386C8.96852 10.4457 8.96852 9.97088 9.26142 9.67798L15.4811 3.45831H11.4583C11.044 3.45831 10.7083 3.12253 10.7083 2.70831C10.7083 2.2941 11.044 1.95831 11.4583 1.95831H17.2916H17.2917C17.2999 1.95831 17.3082 1.95845 17.3165 1.95872C17.5001 1.96475 17.6819 2.03783 17.8221 2.17798ZM3.45825 6.45831C3.45825 5.49169 4.24163 4.70831 5.20825 4.70831H9.37492C9.78913 4.70831 10.1249 4.37253 10.1249 3.95831C10.1249 3.5441 9.78913 3.20831 9.37492 3.20831H5.20825C3.41321 3.20831 1.95825 4.66327 1.95825 6.45831V14.7916C1.95825 16.5867 3.41321 18.0416 5.20825 18.0416H13.5416C15.3366 18.0416 16.7916 16.5867 16.7916 14.7916V10.625C16.7916 10.2108 16.4558 9.87498 16.0416 9.87498C15.6274 9.87498 15.2916 10.2108 15.2916 10.625V14.7916C15.2916 15.7583 14.5082 16.5416 13.5416 16.5416H5.20825C4.24163 16.5416 3.45825 15.7583 3.45825 14.7916V6.45831Z"
      fill="currentColor"
    />
  </svg>
);

const SubMenu = ({
  items,
  onClose,
  onBack,
}: SubMenuProps): JSX.Element | null => {
  const navigate = useNavigate();

  if (!items) return null;

  const handleClick = (item: MenuItem): void => {
    if (item.external && item.path) {
      window.open(item.path, "_blank", "noopener,noreferrer");
      return;
    }
    if (item.path) {
      navigate(item.path, {
        state: {
          iframeUrl: item.iframeUrl,
          title: item.label,
        },
      });
      onClose();
    }
  };

  return (
    <div className={navStyle.submenuContainer}>
      {onBack && (
        <button
          className={`${navStyle.backBtn} lg:hidden mb-2`}
          onClick={onBack}
        >
          ← Back
        </button>
      )}

      {items.map((section: MenuItem) => (
        <div key={section.id} className={navStyle.submenuSection}>
          {section.label && (
            <h3 className={`${navStyle.submenuTitle} title-xxs`}>
              {section.label}
            </h3>
          )}
          {section.subheader && (
            <span className={`${navStyle.submenuSubheader}`}>
              {section.subheader}
            </span>
          )}

          {section.children?.map((item: MenuItem) => {
            const content = (
              <>
                <span className={navStyle.submenuLeft}>
                  {item.icon ? (
                    <img
                      src={item.icon}
                      alt={item.label}
                      className={navStyle.submenuIcon}
                    />
                  ) : (
                    <span
                      className={navStyle.itemWithoutImage}
                      aria-hidden="true"
                    />
                  )}
                  <span className={`${navStyle.submenuLabel} whitespace-nowrap`}>{item.label}</span>
                </span>

                {item.external && (
                  <span className={navStyle.submenuExternal}>
                    <ExternalIcon />
                  </span>
                )}
              </>
            );

            // External link
            if (item.external && item.path) {
              return (
                <a
                  key={item.id}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={navStyle.submenuItem}
                  aria-label={`${item.label} (opens in new tab)`}
                >
                  {content}
                </a>
              );
            }

            // Internal link
            return item.iframeUrl ? (
              <Link
                key={item.id}
                to={item.path || "#"}
                state={{
                  iframeUrl: item.iframeUrl,
                  title: item.label,
                }}
                onClick={onClose}
                className={navStyle.submenuItem}
                aria-label={item.label}
              >
                {content}
              </Link>
            ) : (
              <a
                key={item.id}
                href={item.path || "#"}
                className={navStyle.submenuItem}
                aria-label={item.label}
                role="button"
                tabIndex={0}
              >
                {content}
              </a>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default SubMenu;
